import React, { useState, useEffect, useMemo } from 'react';
import { Briefcase, FolderClosed, PlusCircle, AlertCircle, Plus, X } from 'lucide-react';
import { DragDropContext } from '@hello-pangea/dnd';
import { Card, Alert } from '../ui';
import Switch from '../ui/Switch';
import Button from '../ui/Button';
import { useSalon } from '../../contexts/SalonContext';
import useServices from '../../hooks/useServices';
import useCategories from '../../hooks/useCategories';
import useDragAndDrop from '../../hooks/useDragAndDrop';
import { SavingNotification } from '../ui/FloatingNotification';
import { getServiceProfessionals } from '../../firebase';

// Componentes
import ServiceList from './service-management/ServiceList';
import CategoryList from './service-management/CategoryList';
import ServiceModal from './ServiceModal';
import CategoryModal from './service-management/CategoryModal';
import ConfirmDeleteModal from './service-management/ConfirmDeleteModal';

/**
 * Botão flutuante de ações para adicionar categorias e serviços
 */
const FloatingActionButton = ({ onNewCategory, onNewService, viewMode, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const toggleMenu = () => {
    setIsOpen(prev => !prev);
  };
  
  const handleAction = (action) => {
    setIsOpen(false);
    action();
  };
  
  return (
    <div className="fixed bottom-20 right-4 z-50 flex flex-col items-end space-y-2">
      {/* Menu de opções quando aberto */}
      {isOpen && (
        <>
          <div className="flex items-center bg-white rounded-full shadow-lg py-2 px-4 mb-2 transform transition-all duration-200 ease-out">
            <span className="text-gray-700 text-sm mr-2">{viewMode ? 'Nova Categoria' : 'Novo Serviço'}</span>
            <button
              onClick={() => handleAction(viewMode ? onNewCategory : onNewService)}
              className="w-10 h-10 rounded-full bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center shadow-md transform transition hover:scale-110"
              disabled={disabled}
            >
              <Plus size={20} />
            </button>
          </div>
          
          <div className="flex items-center bg-white rounded-full shadow-lg py-2 px-4 mb-2 transform transition-all duration-200 ease-out">
            <span className="text-gray-700 text-sm mr-2">{viewMode ? 'Novo Serviço' : 'Nova Categoria'}</span>
            <button
              onClick={() => handleAction(viewMode ? onNewService : onNewCategory)}
              className="w-10 h-10 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center shadow-md transform transition hover:scale-110"
              disabled={disabled}
            >
              <Plus size={20} />
            </button>
          </div>
        </>
      )}
      
      {/* Botão principal */}
      <button
        onClick={toggleMenu}
        className={`w-14 h-14 rounded-full ${isOpen ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'} text-white shadow-lg flex items-center justify-center transition-all duration-300 hover:shadow-xl transform hover:scale-105`}
        aria-label={isOpen ? 'Fechar menu' : 'Abrir menu de ações'}
      >
        {isOpen ? <X size={24} /> : <Plus size={24} />}
      </button>
    </div>
  );
};

/**
 * Componente para gerenciar serviços e categorias
 * 
 * @param {Object} props - Propriedades do componente
 * @param {string} props.salonId - ID do salão a ser gerenciado
 */
const ServiceManagement = ({ salonId: propSalonId }) => {
  const { salonId: contextSalonId } = useSalon();
  // Priorizar o salonId recebido como prop, caso contrário usar o do contexto
  const salonId = propSalonId || contextSalonId;
  
  // Estado para controlar qual visualização está ativa (serviços ou categorias)
  const [viewMode, setViewMode] = useState(false); // false = serviços, true = categorias
  
  // Estado para mensagens de erro
  const [operationError, setOperationError] = useState(null);
  
  // Estado para indicar operações em andamento
  const [saving, setSaving] = useState(false);

  // *** MOVER PARA AQUI: Estado para controlar o menu FAB ***
  const [showFabMenu, setShowFabMenu] = useState(false);

  // Hook para serviços
  const { 
    services, 
    loading: servicesLoading, 
    error: servicesError,
    addService,
    updateService,
    deleteService,
    updateServicesOrder
  } = useServices(salonId);
  
  // Hook para categorias
  const { 
    categories, 
    loading: categoriesLoading, 
    error: categoriesError,
    addCategory,
    updateCategory,
    deleteCategory,
    updateCategoriesOrder
  } = useCategories(salonId);
  
  // Estados locais para UI otimista
  const [localServices, setLocalServices] = useState([]);
  const [localCategories, setLocalCategories] = useState([]);
  
  // Sincronizar estados locais quando os dados do banco mudam
  useEffect(() => {
    if (services && services.length > 0) {
      // Verificar se estamos em uma operação de salvamento
      if (saving) {
        return; // Não atualizar durante operações de salvamento
      }
      
      // Evitar atualizações desnecessárias
      if (localServices.length === 0) {
        setLocalServices(services);
      } else {
        // Verificar se há novos serviços no banco que não existem localmente
        const localIds = new Set(localServices.map(s => s.id));
        const newServices = services.filter(s => !localIds.has(s.id));
        
        if (newServices.length > 0) {
          setLocalServices(prev => [...prev, ...newServices]);
        }
      }
    }
  }, [services, localServices, saving]);
  
  useEffect(() => {
    if (categories && categories.length > 0) {
      // Verificar se estamos em uma operação de salvamento
      if (saving) {
        return; // Não atualizar durante operações de salvamento
      }
      
      // Evitar atualizações desnecessárias
      if (localCategories.length === 0) {
        setLocalCategories(categories);
      } else {
        // Verificar se há novas categorias no banco que não existem localmente
        const localIds = new Set(localCategories.map(c => c.id));
        const newCategories = categories.filter(c => !localIds.has(c.id));
        
        if (newCategories.length > 0) {
          setLocalCategories(prev => [...prev, ...newCategories]);
        }
      }
    }
  }, [categories, localCategories, saving]);

  // Hook para drag and drop com UI otimista
  const { handleDragEnd } = useDragAndDrop({
    services,
    categories,
    localServices,
    localCategories,
    setLocalServices,
    setLocalCategories,
    updateService,
    updateServicesOrder,
    updateCategoriesOrder,
    setOperationError,
    setSaving
  });
  
  // Estados para modais
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  
  // Estados para o item selecionado
  const [selectedService, setSelectedService] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [deleteItemType, setDeleteItemType] = useState('service');
  
  // Agrupar serviços por categoria (usando o estado local)
  const servicesByCategory = useMemo(() => {
    return localCategories.map(category => ({
      ...category,
      services: localServices
        .filter(service => 
          service && 
          service.id && 
          (service.categoria === category.id || service.categoryId === category.id)
        )
        .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
    }));
  }, [localCategories, localServices]);

  // Limpar mensagem de erro após alguns segundos
  useEffect(() => {
    if (operationError) {
      const timer = setTimeout(() => {
        setOperationError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [operationError]);

  // Handler para novo serviço
  const handleNewService = () => {
    setSelectedService(null);
    setIsServiceModalOpen(true);
  };

  // Handler para editar serviço
  const handleEditService = async (service) => {
    try {
      // Buscar as associações do serviço com profissionais
      const associations = await getServiceProfessionals(salonId, service.id);
      
      // Converter associações para o formato de customizações
      const professionalCustomizations = {};
      associations.forEach(assoc => {
        if (assoc.customPrice !== null || assoc.customDuration !== null) {
          professionalCustomizations[assoc.professionalId] = {
            customPrice: assoc.customPrice,
            customDuration: assoc.customDuration
          };
        }
      });
      
      // Adicionar as customizações ao serviço
      setSelectedService({
        ...service,
        professionalCustomizations
      });
      
      setIsServiceModalOpen(true);
    } catch (err) {
      console.error('Erro ao carregar customizações do serviço:', err);
      setOperationError('Erro ao carregar customizações do serviço');
    }
  };

  // Handler para salvar serviço com UI otimista
  const handleSaveService = async (serviceData) => {
    try {
      setOperationError(null);
      setSaving(true);
      
      if (selectedService) {
        // UI otimista para atualização de serviço
        const updatedServices = localServices.map(service => 
          service.id === selectedService.id 
            ? { ...service, ...serviceData } 
            : service
        );
        setLocalServices(updatedServices);
        
        // Atualizar no banco em segundo plano
        await updateService(selectedService.id, serviceData)
          .catch(err => {
            console.error('Erro ao atualizar serviço:', err);
            setOperationError(`Erro ao salvar serviço: ${err.message}`);
            // Reverter mudanças locais em caso de erro
            setLocalServices(services);
          });
      } else {
        // Adicionar novo serviço
        const newService = await addService(serviceData);
        // Atualizar estado local após confirmação do banco
        if (newService && newService.id) {
          setLocalServices(prev => [...prev, newService]);
        }
      }
      
      setIsServiceModalOpen(false);
    } catch (err) {
      console.error('Erro ao salvar serviço:', err);
      setOperationError(`Erro ao salvar serviço: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handler para nova categoria
  const handleNewCategory = () => {
    setSelectedCategory(null);
    setIsCategoryModalOpen(true);
  };

  // Handler para editar categoria
  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setIsCategoryModalOpen(true);
  };

  // Handler para salvar categoria com UI otimista
  const handleSaveCategory = async (categoryData) => {
    try {
      setOperationError(null);
      setSaving(true);
      
      if (selectedCategory) {
        // UI otimista para atualização de categoria
        const updatedCategories = localCategories.map(category => 
          category.id === selectedCategory.id 
            ? { ...category, ...categoryData } 
            : category
        );
        setLocalCategories(updatedCategories);
        
        // Atualizar no banco em segundo plano
        await updateCategory(selectedCategory.id, categoryData)
          .catch(err => {
            console.error('Erro ao atualizar categoria:', err);
            setOperationError(`Erro ao salvar categoria: ${err.message}`);
            // Reverter mudanças locais em caso de erro
            setLocalCategories(categories);
          });
      } else {
        // Adicionar nova categoria
        const newCategory = await addCategory(categoryData);
        // Atualizar estado local após confirmação do banco
        if (newCategory && newCategory.id) {
          setLocalCategories(prev => [...prev, newCategory]);
        }
      }
      
      setIsCategoryModalOpen(false);
    } catch (err) {
      console.error('Erro ao salvar categoria:', err);
      setOperationError(`Erro ao salvar categoria: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handler para confirmar exclusão
  const handleDeleteConfirm = (item, type) => {
    if (!item || !item.id) {
      setOperationError('Item inválido selecionado para exclusão');
      return;
    }
    
    if (type === 'service') {
      setSelectedService(item);
    } else {
      setSelectedCategory(item);
    }
    
    setDeleteItemType(type);
    setConfirmDeleteModalOpen(true);
  };

  // Handler para executar exclusão com UI otimista
  const confirmDelete = async () => {
    try {
      setOperationError(null);
      setSaving(true);
      
      if (deleteItemType === 'service' && selectedService) {
        // UI otimista para exclusão
        setLocalServices(prev => prev.filter(service => service.id !== selectedService.id));
        
        // Excluir no banco em segundo plano
        await deleteService(selectedService.id)
          .catch(err => {
            console.error('Erro ao excluir serviço:', err);
            setOperationError(`Erro ao excluir: ${err.message}`);
            // Reverter para o estado original em caso de erro
            setLocalServices(services);
          });
      } else if (deleteItemType === 'category' && selectedCategory) {
        // UI otimista para exclusão
        setLocalCategories(prev => prev.filter(category => category.id !== selectedCategory.id));
        
        // Excluir no banco em segundo plano
        await deleteCategory(selectedCategory.id)
          .catch(err => {
            console.error('Erro ao excluir categoria:', err);
            setOperationError(`Erro ao excluir: ${err.message}`);
            // Reverter para o estado original em caso de erro
            setLocalCategories(categories);
          });
      }
      
      setConfirmDeleteModalOpen(false);
      setSelectedService(null);
      setSelectedCategory(null);
    } catch (err) {
      console.error(`Erro ao excluir ${deleteItemType}:`, err);
      setOperationError(`Erro ao excluir: ${err.message}`);
      setConfirmDeleteModalOpen(false);
    } finally {
      setSaving(false);
    }
  };

  // Estados de loading
  const isLoading = servicesLoading || categoriesLoading;
  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  // Estado de erro
  const error = servicesError || categoriesError || operationError;

  return (
    <div className="relative space-y-4 mt-6">
      {saving && <SavingNotification />}
      
      <Card>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h1 className="text-xl font-medium text-gray-900">
              {viewMode ? 'Categorias' : 'Serviços'}
            </h1>
            
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-500">
                {viewMode ? 'Categorias' : 'Serviços'}
              </span>
              <Switch
                checked={viewMode}
                onChange={() => setViewMode(!viewMode)}
                className="h-6"
              />
              <span className="text-sm text-gray-500">
                {viewMode ? 'Serviços' : 'Categorias'}
              </span>
            </div>
          </div>
          
          {operationError && (
            <Alert type="error" title="Erro" className="mb-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 mr-2 text-red-500" />
                <span>{operationError}</span>
              </div>
            </Alert>
          )}

          {(categoriesLoading || servicesLoading) ? (
            <div className="py-8 flex justify-center">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                <span className="mt-3 text-gray-500">Carregando...</span>
              </div>
            </div>
          ) : categoriesError || servicesError ? (
            <Alert type="error" title="Erro ao carregar dados">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 mr-2 text-red-500" />
                <span>{categoriesError || servicesError}</span>
              </div>
            </Alert>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              {viewMode ? (
                <CategoryList 
                  categories={localCategories}
                  onEditCategory={handleEditCategory}
                  onDeleteCategory={(category) => handleDeleteConfirm(category, 'category')}
                  isSaving={saving}
                />
              ) : (
                <ServiceList 
                  servicesByCategory={servicesByCategory}
                  onEditService={handleEditService}
                  onDeleteService={(service) => handleDeleteConfirm(service, 'service')}
                  isSaving={saving}
                />
              )}
            </DragDropContext>
          )}
          
          {/* Remover os botões aqui */}
        </div>
      </Card>
      
      {/* Adicionar o componente FloatingActionButton */}
      <FloatingActionButton 
        onNewCategory={handleNewCategory} 
        onNewService={handleNewService}
        viewMode={viewMode}
        disabled={saving}
      />
      
      {/* Modais */}
      {isServiceModalOpen && (
        <ServiceModal
          isOpen={isServiceModalOpen}
          onClose={() => setIsServiceModalOpen(false)}
          onSave={handleSaveService}
          service={selectedService}
          categories={localCategories}
          isLoading={saving}
        />
      )}
      
      {isCategoryModalOpen && (
        <CategoryModal
          isOpen={isCategoryModalOpen}
          onClose={() => setIsCategoryModalOpen(false)}
          onSave={handleSaveCategory}
          category={selectedCategory}
          isLoading={saving}
        />
      )}
      
      {confirmDeleteModalOpen && (
        <ConfirmDeleteModal
          isOpen={confirmDeleteModalOpen}
          onClose={() => setConfirmDeleteModalOpen(false)}
          onConfirm={confirmDelete}
          title={`Excluir ${deleteItemType === 'service' ? 'Serviço' : 'Categoria'}`}
          message={`Tem certeza que deseja excluir ${
            deleteItemType === 'service' 
              ? `o serviço "${selectedService?.nome}"?` 
              : `a categoria "${selectedCategory?.nome}" e todos os seus serviços?`
          }`}
          confirmText="Excluir"
          cancelText="Cancelar"
          isLoading={saving}
        />
      )}
    </div>
  );
};

export default ServiceManagement; 